import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { ResultOf } from "@graphql-typed-document-node/core";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconCopy,
  IconDownload,
  IconFingerprint,
  IconHash,
  IconId,
  IconShieldCheck,
  IconUser,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { ReactNode, useState } from "react";
import { useParams } from "react-router-dom";
import {
  EventReservationStatus,
  UserGroupStatus,
} from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { ListPageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";

const EVENT_RESERVATIONS = graphql(`
  query eventReservations(
    $eventId: ID!
    $cursor: String
    $count: Float
    $query: EventReservationsQueryInput!
  ) {
    eventReservations(
      eventId: $eventId
      query: $query
      page: { after: $cursor, first: $count }
    ) {
      nodes {
        id
        name
        groupId
        status
        groupStatus
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const EVENT_RESERVATION = graphql(`
  query eventReservation($reservationId: ID!) {
    eventReservation(reservationId: $reservationId) {
      id
      name
      groupId
      status
      groupStatus
    }
  }
`);

const EVENT_RESERVATION_UPDATE_STATUS = graphql(`
  mutation eventReservationUpdateStatus(
    $reservationId: ID!
    $status: EventReservationStatus!
    $confirmPaymentRefundOrCancel: Boolean
    $refundPayment: Boolean
  ) {
    eventReservationUpdateStatus(
      reservationId: $reservationId
      status: $status
      confirmPaymentRefundOrCancel: $confirmPaymentRefundOrCancel
      refundPayment: $refundPayment
    ) {
      id
    }
  }
`);

type RefundChoice = "refund" | "no-refund" | "cancel";

const RefundChoiceContent = ({
  onChoice,
}: {
  onChoice: (choice: RefundChoice) => void;
}) => (
  <Stack gap="md">
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "var(--mantine-spacing-md)",
      }}
    >
      <ThemeIcon size={40} radius="xl" variant="light" color="rose">
        <IconAlertTriangle size={20} />
      </ThemeIcon>
    </div>
    <Text ta="center" c="dark.2" style={{ lineHeight: 1.6 }}>
      You are about to cancel the payment for this reservation. Confirming the
      refund will return the payment to the user — this action is irreversible.
      You can also change the status without refunding the payment.
    </Text>
    <Group justify="center">
      <Button variant="default" onClick={() => onChoice("cancel")}>
        Cancel
      </Button>
      <Button variant="light" onClick={() => onChoice("no-refund")}>
        Without refund
      </Button>
      <Button color="rose" onClick={() => onChoice("refund")}>
        Confirm Refund
      </Button>
    </Group>
  </Stack>
);

const askRefundChoice = (): Promise<RefundChoice> =>
  new Promise<RefundChoice>((resolve) => {
    const modalId = "reservation-refund-choice";
    let resolved = false;
    const choose = (choice: RefundChoice) => {
      resolved = true;
      modals.close(modalId);
      resolve(choice);
    };
    modals.open({
      modalId,
      title: "Confirm Payment Refund",
      centered: true,
      withCloseButton: false,
      size: "sm",
      radius: "lg",
      padding: "lg",
      onClose: () => {
        if (!resolved) {
          resolve("cancel");
        }
      },
      children: <RefundChoiceContent onChoice={choose} />,
    });
  });

const EVENT_GROUP_MEMBERS = graphql(`
  query eventGroupMembers($groupIds: [ID!]!, $page: PageInfo!) {
    userGroupMembers(groupIds: $groupIds, page: $page, status: ACCEPTED) {
      nodes {
        id
        playerProfileId
        groupId
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const PLAYER_PROFILES = graphql(`
  query playerProfiles($playerProfileIds: [ID!]!, $page: PageInfo!) {
    players(ids: $playerProfileIds, page: $page) {
      nodes {
        id
        username
        customFields {
          property
          name
          value
          type
          order
        }
        identities {
          organizationIdentityProvider {
            name
          }
          properties {
            property
            value
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const CopyIdChip = ({
  id,
  label,
  maxWidth,
}: {
  id: string;
  label?: string;
  maxWidth?: number;
}) => (
  <CopyButton value={id} timeout={1500}>
    {({ copied, copy }) => (
      <Tooltip
        label={copied ? "Copied to clipboard" : (label ?? "Copy ID")}
        withArrow
      >
        <Group
          gap={6}
          wrap="nowrap"
          onClick={copy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              copy();
            }
          }}
          style={{
            cursor: "pointer",
            padding: "3px 8px",
            borderRadius: 6,
            backgroundColor: "var(--mantine-color-dark-6)",
            border: `1px solid var(--mantine-color-dark-4)`,
            transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
            maxWidth: "100%",
            userSelect: "none",
          }}
        >
          <Text
            size="xs"
            ff="monospace"
            c={copied ? "green.4" : "dimmed"}
            truncate
            style={{ maxWidth: maxWidth ?? 200 }}
          >
            {id}
          </Text>
          {copied ? (
            <IconCheck
              size={12}
              style={{ color: "var(--mantine-color-green-4)" }}
            />
          ) : (
            <IconCopy size={12} style={{ opacity: 0.7 }} />
          )}
        </Group>
      </Tooltip>
    )}
  </CopyButton>
);

const StatTile = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <Paper
    p="sm"
    radius="md"
    withBorder
    style={{
      backgroundColor: "var(--mantine-color-dark-7)",
      borderColor: "var(--mantine-color-dark-5)",
    }}
  >
    <Group gap={6} mb={6}>
      <ThemeIcon size={18} variant="subtle" color="cyan" radius="sm">
        {icon}
      </ThemeIcon>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.06em">
        {label}
      </Text>
    </Group>
    {children}
  </Paper>
);

const SectionLabel = ({
  icon,
  label,
  count,
}: {
  icon: ReactNode;
  label: string;
  count: number;
}) => (
  <Group gap={6} mb="xs">
    <ThemeIcon size={16} variant="subtle" color="cyan" radius="xs">
      {icon}
    </ThemeIcon>
    <Text size="xs" tt="uppercase" fw={700} lts="0.08em" c="cyan.4">
      {label}
    </Text>
    <Badge size="xs" variant="default" radius="sm">
      {count}
    </Badge>
  </Group>
);

const getInitials = (username: string): string =>
  username
    .split(/[\s_\-.]+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const customFieldTypeColor: Record<string, string> = {
  STRING: "cyan",
  NUMBER: "cyan",
  BOOLEAN: "green",
  DATE: "purple",
  EMAIL: "purple",
  PHONE: "purple",
  URL: "cyan",
  COUNTRY: "amber",
};

const renderCustomFieldValue = (field: {
  type?: string | null;
  value?: string | null;
}): ReactNode => {
  const value = field.value;
  if (value === null || value === undefined || value === "") {
    return (
      <Text span size="sm" c="dimmed" fs="italic">
        —
      </Text>
    );
  }
  switch (field.type) {
    case "BOOLEAN":
      return (
        <Badge
          size="sm"
          radius="sm"
          variant="light"
          color={value === "true" ? "green" : "dark"}
        >
          {value === "true" ? "Yes" : "No"}
        </Badge>
      );
    case "DATE": {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? (
        <Text size="sm" style={{ wordBreak: "break-word" }}>
          {value}
        </Text>
      ) : (
        <Text size="sm">{date.toLocaleString()}</Text>
      );
    }
    case "URL":
      return (
        <Text
          component="a"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          c="cyan.4"
          style={{ wordBreak: "break-all" }}
        >
          {value}
        </Text>
      );
    case "EMAIL":
      return (
        <Text
          component="a"
          href={`mailto:${value}`}
          size="sm"
          c="cyan.4"
          style={{ wordBreak: "break-all" }}
        >
          {value}
        </Text>
      );
    case "NUMBER":
      return (
        <Text size="sm" ff="monospace">
          {value}
        </Text>
      );
    default:
      return (
        <Text size="sm" style={{ wordBreak: "break-word" }}>
          {value}
        </Text>
      );
  }
};

const PlayerCard = ({ player }: { player: any }) => {
  const identities = player.identities ?? [];
  const customFields = [...(player.customFields ?? [])].sort(
    (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
  );
  const initials = getInitials(player.username ?? "");

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="center">
          <Avatar
            size={44}
            radius="md"
            variant="gradient"
            gradient={{ from: "cyan.6", to: "purple.6", deg: 135 }}
          >
            {initials || <IconUser size={20} />}
          </Avatar>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="md" truncate>
              {player.username}
            </Text>
            <CopyIdChip id={player.id} label="Copy player ID" maxWidth={240} />
          </Stack>
        </Group>

        {identities.length > 0 && (
          <Box>
            <SectionLabel
              icon={<IconFingerprint size={11} />}
              label="Identities"
              count={identities.length}
            />
            <Stack gap={6}>
              {identities.map((identity: any, idx: number) => (
                <Paper
                  key={`${identity.organizationIdentityProvider?.name ?? "unknown"}-${idx}`}
                  p="xs"
                  radius="sm"
                  style={{
                    backgroundColor: "var(--mantine-color-dark-8)",
                    border: "1px solid var(--mantine-color-dark-5)",
                  }}
                >
                  <Group gap="xs" mb={identity.properties?.length ? 6 : 0}>
                    <Badge size="sm" color="purple" variant="light" radius="sm">
                      {identity.organizationIdentityProvider?.name ??
                        "Unknown provider"}
                    </Badge>
                  </Group>
                  {identity.properties?.length > 0 && (
                    <Stack gap={4}>
                      {identity.properties.map((prop: any) => (
                        <Group
                          key={prop.property}
                          gap="sm"
                          wrap="nowrap"
                          align="flex-start"
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            fw={500}
                            tt="lowercase"
                            style={{ minWidth: 96, flexShrink: 0 }}
                          >
                            {prop.property}
                          </Text>
                          <Text
                            size="xs"
                            ff="monospace"
                            style={{ wordBreak: "break-all" }}
                          >
                            {prop.value}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {customFields.length > 0 && (
          <Box>
            <SectionLabel
              icon={<IconId size={11} />}
              label="Custom fields"
              count={customFields.length}
            />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              {customFields.map((field: any) => (
                <Paper
                  key={field.property ?? field.name}
                  p="xs"
                  radius="sm"
                  style={{
                    backgroundColor: "var(--mantine-color-dark-8)",
                    border: "1px solid var(--mantine-color-dark-5)",
                  }}
                >
                  <Group gap={6} mb={4} wrap="nowrap" justify="space-between">
                    <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        lts="0.05em"
                        truncate
                      >
                        {field.name}
                      </Text>
                      {field.property && field.property !== field.name && (
                        <Text size="10px" ff="monospace" c="dark.2" truncate>
                          {field.property}
                        </Text>
                      )}
                    </Stack>
                    {field.type && (
                      <Badge
                        size="xs"
                        radius="sm"
                        variant="light"
                        color={customFieldTypeColor[field.type] ?? "dark"}
                      >
                        {field.type}
                      </Badge>
                    )}
                  </Group>
                  {renderCustomFieldValue(field)}
                </Paper>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {identities.length === 0 && customFields.length === 0 && (
          <Text size="xs" c="dimmed" fs="italic" ta="center" py="xs">
            No identities or custom fields recorded for this player.
          </Text>
        )}
      </Stack>
    </Card>
  );
};

const ReservationInfoSkeleton = () => (
  <Stack gap="lg">
    <Skeleton height={104} radius="md" />
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
      <Skeleton height={70} radius="md" />
      <Skeleton height={70} radius="md" />
      <Skeleton height={70} radius="md" />
    </SimpleGrid>
    <Skeleton height={16} width="40%" radius="sm" />
    <Skeleton height={180} radius="md" />
    <Skeleton height={180} radius="md" />
  </Stack>
);

const ReservationInfo = ({ reservationId }: { reservationId: string }) => {
  const { data, loading } = useQuery<any>(EVENT_RESERVATION, {
    variables: { reservationId },
  });
  const { data: groupMembers, loading: groupMembersLoading } = useQuery<any>(
    EVENT_GROUP_MEMBERS,
    {
      variables: {
        groupIds: [data?.eventReservation?.groupId ?? ""],
        page: { first: 1000 },
      },
      skip: !data?.eventReservation?.groupId,
    },
  );
  const { data: players, loading: playersLoading } = useQuery<any>(
    PLAYER_PROFILES,
    {
      variables: {
        playerProfileIds:
          groupMembers?.userGroupMembers.nodes.map(
            (node: any) => node.playerProfileId,
          ) ?? [],
        page: { first: 1000 },
      },
      skip: !groupMembers?.userGroupMembers?.nodes.length,
    },
  );

  if (loading || groupMembersLoading || playersLoading) {
    return <ReservationInfoSkeleton />;
  }

  if (!data?.eventReservation) {
    return (
      <Paper
        p="xl"
        radius="md"
        ta="center"
        withBorder
        style={{ backgroundColor: "var(--mantine-color-dark-7)" }}
      >
        <ThemeIcon size={42} variant="light" color="rose" mx="auto" mb="sm">
          <IconUsersGroup size={22} />
        </ThemeIcon>
        <Text c="dimmed">This reservation could not be found.</Text>
      </Paper>
    );
  }

  const reservation = data.eventReservation;
  const playerList = players?.players.nodes ?? [];
  const groupSize = groupMembers?.userGroupMembers.nodes.length ?? 0;
  const isGroupValid = reservation.groupStatus === UserGroupStatus.Valid;

  return (
    <Stack gap="lg">
      <Paper
        p="lg"
        radius="md"
        style={{
          background:
            "linear-gradient(135deg, var(--mantine-color-dark-6) 0%, var(--mantine-color-dark-7) 100%)",
          border: "1px solid var(--mantine-color-dark-4)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, var(--mantine-color-cyan-9) 0%, transparent 70%)",
            opacity: 0.35,
            pointerEvents: "none",
          }}
        />
        <Group
          gap="md"
          wrap="nowrap"
          align="flex-start"
          style={{ position: "relative" }}
        >
          <ThemeIcon size={52} radius="md" variant="light" color="cyan">
            <IconUsersGroup size={28} />
          </ThemeIcon>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Text
              size="xs"
              tt="uppercase"
              fw={600}
              c="dimmed"
              lts="0.12em"
            >
              Reservation
            </Text>
            <Title
              order={3}
              style={{
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
            >
              {reservation.name}
            </Title>
            <Group gap="xs" mt={6} wrap="wrap">
              <StatusBadge
                status={reservation.status}
                label={StatusLabels[reservation.status as EventReservationStatus]}
              />
              <CopyIdChip
                id={reservation.id}
                label="Copy reservation ID"
                maxWidth={220}
              />
            </Group>
          </Stack>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <StatTile
          icon={<IconShieldCheck size={14} />}
          label="Group status"
        >
          <StatusBadge
            status={reservation.groupStatus}
            color={isGroupValid ? "green" : "rose"}
          />
        </StatTile>
        <StatTile icon={<IconUsers size={14} />} label="Roster size">
          <Group gap={6} align="baseline">
            <Text fw={700} size="xl" lh={1}>
              {groupSize}
            </Text>
            <Text size="sm" c="dimmed" fw={500}>
              {groupSize === 1 ? "member" : "members"}
            </Text>
          </Group>
        </StatTile>
        <StatTile icon={<IconHash size={14} />} label="Group ID">
          <CopyIdChip
            id={reservation.groupId}
            label="Copy group ID"
            maxWidth={180}
          />
        </StatTile>
      </SimpleGrid>

      <Stack gap="sm">
        <Group gap="sm" align="center" wrap="nowrap">
          <Divider style={{ flex: 1 }} />
          <Group gap={6}>
            <IconUser size={12} style={{ opacity: 0.6 }} />
            <Text
              size="xs"
              fw={700}
              tt="uppercase"
              lts="0.12em"
              c="dimmed"
            >
              Roster · {playerList.length}
            </Text>
          </Group>
          <Divider style={{ flex: 1 }} />
        </Group>

        {playerList.length === 0 ? (
          <Paper
            p="xl"
            radius="md"
            ta="center"
            withBorder
            style={{ backgroundColor: "var(--mantine-color-dark-7)" }}
          >
            <ThemeIcon
              size={40}
              variant="light"
              color="dark"
              mx="auto"
              mb="sm"
            >
              <IconUser size={20} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              No players are registered on this reservation yet.
            </Text>
          </Paper>
        ) : (
          <Stack gap="md">
            {playerList.map((player: any) => (
              <PlayerCard key={player.id} player={player} />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const StatusLabels: Record<EventReservationStatus, string> = {
  [EventReservationStatus.Confirmed]: "Confirmed and Participating",
  [EventReservationStatus.Denied]: "Denied",
  [EventReservationStatus.None]: "Reservation created",
  [EventReservationStatus.NotAttending]: "Not attending",
  [EventReservationStatus.PaymentFailed]: "Payment failed",
  [EventReservationStatus.PaymentPending]: "Payment pending",
  [EventReservationStatus.PaymentRefunded]: "Payment refunded",
  [EventReservationStatus.Pending]:
    "In payment session (can still update reservation)",
  [EventReservationStatus.PresenceConfirmationPending]:
    "Presence confirmation pending",
  [EventReservationStatus.Registered]:
    "Registered, payment validated, waiting for admin approval",
  [EventReservationStatus.RequiresAdminApproval]:
    "Requires admin approval, before payment",
  [EventReservationStatus.SessionExpired]: "Session expired",
};

export const EventReservations = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const eventId = useParams().eventId!;
  const [status, setStatus] = useState<EventReservationStatus | null>(null);
  const [groupStatus, setGroupStatus] = useState<UserGroupStatus | null>(null);
  const [updateStatus] = useMutation<any>(EVENT_RESERVATION_UPDATE_STATUS);
  const [reservations] = useLazyQuery<any>(EVENT_RESERVATIONS);
  const [exporting, setExporting] = useState(false);
  const [groupMembers] = useLazyQuery<any>(EVENT_GROUP_MEMBERS);
  const [playerProfiles] = useLazyQuery<any>(PLAYER_PROFILES);

  const handleExport = async () => {
    setExporting(true);
    try {
      const reservationsData: Record<
        string,
        {
          reservation: ResultOf<
            typeof EVENT_RESERVATIONS
          >["eventReservations"]["nodes"][number];
          players: ResultOf<typeof PLAYER_PROFILES>["players"]["nodes"];
        }
      > = {};
      let reservationsNextPageCursor: string | boolean = true;
      while (reservationsNextPageCursor) {
        const { data, error } = await reservations({
          variables: {
            eventId,
            query: {
              filterByRegistrationStatus: status,
              filterByGroupStatus: groupStatus,
            },
            cursor:
              reservationsNextPageCursor !== true
                ? reservationsNextPageCursor
                : null,
            count: 1000,
          },
        });
        if (error) {
          throw new Error(error.message);
        }
        reservationsNextPageCursor =
          data?.eventReservations.pageInfo.endCursor ?? false;

        for (const reservation of data?.eventReservations.nodes ?? []) {
          reservationsData[reservation.groupId] = {
            reservation,
            players: [],
          };
        }

        let nextMembersCursor: string | boolean = true;
        while (nextMembersCursor) {
          const { data: groupMembersData, error } = await groupMembers({
            variables: {
              groupIds:
                data?.eventReservations.nodes.map(
                  (reservation: any) => reservation.groupId,
                ) ?? [],
              page: {
                after: nextMembersCursor !== true ? nextMembersCursor : null,
                first: 1000,
              },
            },
          });
          if (error) {
            throw new Error(error.message);
          }
          nextMembersCursor =
            groupMembersData?.userGroupMembers.pageInfo.endCursor ?? false;

          let nextPlayersCursor: string | boolean = true;
          while (nextPlayersCursor) {
            const { data: playersData, error } = await playerProfiles({
              variables: {
                playerProfileIds:
                  groupMembersData?.userGroupMembers.nodes.map(
                    (member: any) => member.playerProfileId,
                  ) ?? [],
                page: {
                  after: nextPlayersCursor !== true ? nextPlayersCursor : null,
                  first: 1000,
                },
              },
            });
            if (error) {
              throw new Error(error.message);
            }
            nextPlayersCursor =
              playersData?.players.pageInfo.endCursor ?? false;

            for (const playerMember of groupMembersData?.userGroupMembers
              .nodes ?? []) {
              const playerData = playersData?.players.nodes.find(
                (player: any) => player.id === playerMember.playerProfileId,
              );
              if (playerData) {
                reservationsData[playerMember.groupId].players.push(playerData);
              }
            }
          }
        }
      }

      let maxPlayersPerReservation = 0;
      const customFieldsTitles = Array.from(
        new Set(
          Object.values(reservationsData).flatMap((reservation) => {
            maxPlayersPerReservation = Math.max(
              maxPlayersPerReservation,
              reservation.players.length,
            );
            return reservation.players.flatMap((player) =>
              player.customFields.map((field) => field.name),
            );
          }),
        ),
      );
      const sanitizeCsvCell = (value: string): string => {
        if (/^[=+\-@\t\r]/.test(value)) {
          return `'${value.replace(/"/g, '""')}`;
        }
        if (
          value.includes(",") ||
          value.includes('"') ||
          value.includes("\n")
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      const csv = Object.values(reservationsData)
        .map(
          (reservation) =>
            `${sanitizeCsvCell(reservation.reservation.id)},${sanitizeCsvCell(
              reservation.reservation.name,
            )},${sanitizeCsvCell(reservation.reservation.groupStatus)},${reservation.players
              .map(
                (player) =>
                  `${sanitizeCsvCell(player.id)},${sanitizeCsvCell(player.username)},${customFieldsTitles
                    .map((title) =>
                      sanitizeCsvCell(
                        player.customFields.find(
                          (field) => field.name === title,
                        )?.value ?? "N/A",
                      ),
                    )
                    .join(",")}`,
              )
              .join(",")}`,
        )
        .join("\n");
      const columnTitles = [
        "Reservation ID",
        "Reservation Name",
        "Group Status",
      ];
      for (let i = 0; i < maxPlayersPerReservation; i++) {
        columnTitles.push(`Player ${i + 1} ID`);
        columnTitles.push(`Player ${i + 1} Username`);
        for (const title of customFieldsTitles) {
          columnTitles.push(`Player ${i + 1} - ${title}`);
        }
      }
      const blob = new Blob([`${columnTitles.join(",")}\n${csv}`], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reservations-${eventId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: (error as Error).message,
        color: "red",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Reservations"
        showBreadcrumbs={false}
        actions={[
          {
            label: "Export CSV",
            icon: <IconDownload size={16} />,
            onClick: handleExport,
            loading: exporting,
            variant: "light",
          },
        ]}
      />
      <Stack gap="md">
        <Group gap="md">
          <Select
            label="Filter by status"
            data={Object.values(EventReservationStatus)}
            onChange={(value) => {
              setStatus(value as EventReservationStatus);
            }}
            clearable
            placeholder="All statuses"
            style={{ flex: 1 }}
          />
          <Select
            label="Filter by group status"
            data={Object.values(UserGroupStatus)}
            onChange={(value) => {
              setGroupStatus(value as UserGroupStatus);
            }}
            clearable
            placeholder="All group statuses"
            style={{ flex: 1 }}
          />
        </Group>
        <AutoDataTable
          createButtonText={false}
          linkClick={(reservation) => {
            modals.open({
              title: (
                <Group gap="xs" wrap="nowrap">
                  <ThemeIcon
                    size={28}
                    radius="md"
                    variant="light"
                    color="cyan"
                  >
                    <IconUsersGroup size={16} />
                  </ThemeIcon>
                  <Text fw={600} size="md">
                    Reservation details
                  </Text>
                </Group>
              ),
              size: "xl",
              padding: "lg",
              children: <ReservationInfo reservationId={reservation.id} />,
            });
          }}
          query={EVENT_RESERVATIONS}
          queryVariables={{
            eventId,
            query: {
              filterByRegistrationStatus: status,
              filterByGroupStatus: groupStatus,
            },
          }}
          columns={[
            {
              accessor: "name",
              title: "Name",
            },
            {
              accessor: "status",
              title: "Status",
              rawElement: (
                reservation: ResultOf<
                  typeof EVENT_RESERVATIONS
                >["eventReservations"]["nodes"][number],
              ) => {
                return (
                  <Select
                    data={Object.values(EventReservationStatus).map(
                      (statusVal) => ({
                        label: StatusLabels[statusVal],
                        value: statusVal,
                        disabled:
                          statusVal === EventReservationStatus.PaymentPending ||
                          statusVal === EventReservationStatus.PaymentFailed ||
                          statusVal === EventReservationStatus.PaymentRefunded,
                      }),
                    )}
                    defaultValue={reservation.status}
                    onChange={async (value) => {
                      await updateStatus({
                        variables: {
                          reservationId: reservation.id,
                          status: value as EventReservationStatus,
                        },
                      }).then(async (data) => {
                        if (data?.error) {
                          if (
                            data.error.message?.startsWith(
                              "confirmPaymentRefundOrCancel",
                            )
                          ) {
                            const choice = await askRefundChoice();

                            if (choice !== "cancel") {
                              await updateStatus({
                                variables: {
                                  reservationId: reservation.id,
                                  status: value as EventReservationStatus,
                                  confirmPaymentRefundOrCancel: true,
                                  refundPayment: choice === "refund",
                                },
                              }).then((data) => {
                                if (data?.error) {
                                  throw new Error(data.error.message);
                                }
                              });
                            }
                            return;
                          }
                          throw new Error(data.error.message);
                        }
                      });
                      notifications.show({
                        title: "Success",
                        message: "Reservation status updated",
                        color: "green",
                        autoClose: 3000,
                      });
                    }}
                  />
                );
              },
            },
            {
              accessor: "groupStatus",
              title: "Group status",
              rawElement: (
                reservation: ResultOf<
                  typeof EVENT_RESERVATIONS
                >["eventReservations"]["nodes"][number],
              ) => {
                return (
                  <StatusBadge
                    status={reservation.groupStatus}
                    color={
                      reservation.groupStatus === UserGroupStatus.Valid
                        ? "green"
                        : "red"
                    }
                  />
                );
              },
            },
          ]}
        />
      </Stack>
    </div>
  );
};
